import React, { useCallback, useState } from 'react';
import { Loader, Select } from '@mantine/core';
import { translate } from '@shakkerai/util-i18n';
import { Icon } from '@shakkerai/components';
import { GuidePopover, notifySuccess, NumberInputGroup } from '@shakkerai/pro-components';
import { DropZone } from './dropzone';
import { useLoadings } from '@shakkerai/pro-store';
import { updateControlNet } from '../../helpers';
import { pickLocalFiles } from '@shakkerai/util-file';
import {
  resizeModeOption,
  controlWeightOption,
  controlRangeOption,
  MAX_GENERATOR_SIZE,
} from '@shakkerai/pro-constants';
import { facialReferenceDefaultValues } from '@shakkerai/pro-constants';
import { ACCEPTED_FILE_EXTS, findBestSizeByReferenceSize } from '@shakkerai/pro-utils';
import { applyImg2ImgSourceImage, fixHiresOptions, updatePromptFromImage, uploadControlNetImage } from '../../helpers';
import { updateUnitData } from '../../../../helpers/flow';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import imgFaceUploadTip from './assets/face_upload_tip.svg';

import imgFacialFront from './assets/facial_front.png';
import imgFacialRight from './assets/facial_right.png';
import imgFacialLeft from './assets/facial_left.png';
import imgFacialPortrait from './assets/facial_portrait.png';
import imgFacialHalfbody from './assets/facial_halfbody.png';
import imgFacialFullbody from './assets/facial_fullbody.png';
import { setTempState } from '@starflow/app/store';

export const emptyPlaceholder = (
  <>
    <img src={imgFaceUploadTip} className="w-16 h-16 mb-3" />
    <span className="text-sm text-[#7f7f7f]">{translate('cn_face_img1_tips')}</span>
  </>
);

const faceEmbedingOptions = [controlWeightOption, controlRangeOption[0], controlRangeOption[1], resizeModeOption].map(
  (item) => ({
    ...item,
    name: `faceEmbeding.${item.name}`,
  }),
);

const faceKeypointsOptions = [controlWeightOption, controlRangeOption[0], controlRangeOption[1], resizeModeOption].map(
  (item) => ({
    ...item,
    name: `faceKeypoints.${item.name}`,
  }),
);

const referenceTypeOptions = [
  {
    value: 'preset',
    label: translate('cn_face_ref_preset'),
  },
  {
    value: 'same',
    label: translate('cn_face_ref_same'),
  },
  {
    value: 'custom',
    label: translate('cn_face_ref_custom'),
  },
];

const facePresetOptions = [
  {
    value: 'front',
    label: translate('cn_face_ref_front'),
    icon: imgFacialFront,
  },
  {
    value: 'right',
    label: translate('cn_face_ref_right'),
    icon: imgFacialRight,
  },
  {
    value: 'left',
    label: translate('cn_face_ref_left'),
    icon: imgFacialLeft,
  },
];

const bodyPresetOptions = [
  {
    value: 'halfbody',
    label: translate('cn_face_ref_halfbody'),
    icon: imgFacialHalfbody,
  },
  {
    value: 'portrait',
    label: translate('cn_face_ref_portrait'),
    icon: imgFacialPortrait,
  },
  {
    value: 'fullbody',
    label: translate('cn_face_ref_fullbody'),
    icon: imgFacialFullbody,
  },
];

const Options = React.memo(({ options, controlNet, onChange }) => {
  const handleChange = useCallback(
    (name, value) => {
      onChange(name, value);
    },
    [onChange],
  );

  const handleChangeNumber = useCallback(
    (name, value) => {
      onChange(name, Number(value.toFixed(2)));
    },
    [onChange],
  );

  return (
    <div className="grid grid-cols-2 gap-y-3 gap-x-8 p-4 pt-0">
      {options.map((item) => {
        const value =
          typeof controlNet[item.name] === 'undefined'
            ? facialReferenceDefaultValues[item.name]
            : controlNet[item.name];

        if (item.type === 'number') {
          return (
            <div key={item.name} className="flex items-center justify-between h-6">
              {item.label && (
                <span className="w-20 text-ellipsis whitespace-nowrap text-[#7f7f7f] font-grotesk text-xs">
                  {item.label}
                </span>
              )}
              <NumberInputGroup
                size="xs"
                step={item.step}
                min={item.min}
                max={item.max}
                inputWidth={42}
                value={value}
                onChange={(value) => handleChangeNumber(item.name, value)}
              />
            </div>
          );
        }

        if (item.type === 'select') {
          return (
            <div key={item.name} className="flex items-center justify-between h-6">
              {item.label && (
                <span className="w-20 text-ellipsis whitespace-nowrap text-[#7f7f7f] font-grotesk text-xs">
                  {item.label}
                </span>
              )}
              <Select
                size="xxs"
                value={value + ''}
                name={item.name}
                classNames={{
                  dropdown: 'cn-select-dropdown',
                  input: 'text-right pr-6',
                }}
                withScrollArea={false}
                comboboxProps={{ width: 200, position: 'bottom-end' }}
                className={`cn-select text-xs flex-1 w-20`}
                onChange={(value) => handleChange(item.name, value)}
                data={item.options}
              />
            </div>
          );
        }

        return null;
      })}
    </div>
  );
});

export const FaceReference = React.memo(({ isSimple, generatorId, generator, controlNet }) => {
  const { referenceType, faceType, bodyType, sourceImage, referenceImage } = controlNet;
  const referenceImageUploading = useLoadings(`${generatorId}-controlnet-referenceImage-${controlNet.id}-uploading`);
  const [guideVisible, setGuideVisible] = useState(false);
  const [advancedParamsVisible, setAdvancedParamsVisible] = useState(false);

  const handlePickReferenceImage = useCallback(
    async (event) => {
      const [image] = await pickLocalFiles(ACCEPTED_FILE_EXTS);
      if (image) {
        uploadControlNetImage(generatorId, controlNet.id, image, 'referenceImage');
      }

      event.stopPropagation();
    },
    [generatorId, controlNet.id],
  );

  const handleChangeOption = useCallback(
    (name, value) => {
      updateControlNet(generatorId, controlNet.id, { [name]: value });
    },
    [generatorId, controlNet.id],
  );

  const handleUpdateOption = useCallback(
    (event) => {
      const { name, value } = event.currentTarget.dataset;

      if (name) {
        updateControlNet(generatorId, controlNet.id, { [name]: value });
      }

      event.stopPropagation();
    },
    [generatorId, controlNet.id],
  );

  const handleDeleteReferenceImage = useCallback(
    (event) => {
      updateControlNet(generatorId, controlNet.id, { referenceImage: null });
      event.stopPropagation();
    },
    [generatorId, controlNet.id],
  );

  const handleUseAsImg2Img = useCallback(
    (event) => {
      applyImg2ImgSourceImage(referenceImage, generatorId);
      setTempState({ exclusiveWindow: `image-input-options-${generatorId}` });
      updatePromptFromImage(referenceImage, generatorId);

      const fixedSize = findBestSizeByReferenceSize(referenceImage?.width, referenceImage?.height, MAX_GENERATOR_SIZE);

      if (fixedSize.width !== referenceImage?.width || fixedSize.height !== referenceImage?.height) {
        notifySuccess(translate('generator_resize_toast'));
      }

      updateUnitData(generatorId, fixedSize);
      fixHiresOptions(generatorId, 'generateSize');

      event.stopPropagation();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generatorId, referenceImage],
  );

  const showAdvancedParams = advancedParamsVisible && !isSimple;

  return (
    <div>
      <div className="flex w-full items-center justify-between h-[46px] px-4 border-t border-[#efefef]">
        <span className="text-xs text-[#2d2d2d] font-semibold">{translate('cn_face_upload_image')}</span>
      </div>
      <div className="flex items-stretch h-[260px] bg-[#F8F9FA] overflow-hidden">
        <div className="relative w-[260px] flex-shrink-0">
          <DropZone
            sourceImageOnly
            generatorId={generatorId}
            generator={generator}
            controlNet={controlNet}
            emptyPlaceholder={emptyPlaceholder}
          />
          <span className="absolute top-2 right-2 px-3 py-1 bg-black/50 rounded-[2px] text-white text-xs">
            {translate('cn_face_img1')}
          </span>
        </div>
        <div className="relative w-[260px] flex-shrink-0 flex flex-col p-3">
          <span className="text-xs font-semibold text-[#2d2d2d]">{translate('cn_face_img2')}</span>
          <div className="flex items-center mt-2 space-x-2 h-[26px]">
            {referenceTypeOptions.map((item) => (
              <button
                key={item.value}
                data-value={item.value}
                data-name="referenceType"
                disabled={referenceType === item.value}
                onClick={handleUpdateOption}
                className="group flex items-center"
              >
                <div className="w-3 h-3 rounded-full bg-white border border-[#dfd7db] group-disabled:border-[4px] group-disabled:border-primary" />
                <span className="ml-1 text-xs text-[#2d2d2d]">{item.label}</span>
              </button>
            ))}
          </div>
          {referenceType === 'preset' && (
            <div className="flex items-stretch">
              <div className="flex-1 mr-1 p-2 space-y-2 bg-white rounded">
                {facePresetOptions.map((item) => (
                  <button
                    key={item.value}
                    data-name="faceType"
                    data-value={item.value}
                    onClick={handleUpdateOption}
                    disabled={faceType === item.value}
                    className="flex items-center w-full overflow-hidden border border-white rounded text-xs text-[#7f7f7f] disabled:text-[#005848] disabled:font-semibold disabled:border-primary disabled:bg-primary/10"
                  >
                    <img src={item.icon} className="w-9 h-12 mr-2" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
              <div className="flex-1 ml-1 p-2 space-y-2 bg-white rounded">
                {bodyPresetOptions.map((item) => (
                  <button
                    key={item.value}
                    data-name="bodyType"
                    data-value={item.value}
                    onClick={handleUpdateOption}
                    disabled={bodyType === item.value}
                    className="flex items-center w-full overflow-hidden border border-white rounded text-xs text-[#7f7f7f] disabled:text-[#005848] disabled:font-semibold disabled:border-primary disabled:bg-primary/10"
                  >
                    <img src={item.icon} className="w-9 h-12 mr-2" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {referenceType === 'same' && (
            <div className="flex flex-1 items-stretch overflow-hidden mt-3">
              {sourceImage?.url ? (
                <img src={convert2CompressedUrl(sourceImage.url, 400)} className="flex-1 object-contain" />
              ) : (
                <div className="flex flex-col items-center justify-center w-full mb-4 overflow-hidden">
                  <img src={imgFaceUploadTip} className="w-16 h-16 mb-3" />
                  <span className="text-xs text-[#7f7f7f]">{translate('请先上传长相参考图')}</span>
                </div>
              )}
            </div>
          )}
          {referenceType === 'custom' &&
            (referenceImageUploading ? (
              <div className="flex flex-1 w-full items-center justify-center">
                <Loader size={18} />
              </div>
            ) : referenceImage ? (
              <div className="relative flex flex-1 items-stretch mt-3 overflow-hidden">
                <img
                  draggable={false}
                  src={convert2CompressedUrl(referenceImage.url)}
                  className="flex-1 w-full object-contain"
                />
                <div className="flex absolute bottom-3 right-0 left-0 justify-center space-x-2.5">
                  <button
                    onClick={handleDeleteReferenceImage}
                    className="flex-shrink-0 px-3 py-1 bg-black/50 border border-white/30 rounded-[2px] text-white text-xs"
                  >
                    {translate('common_delete')}
                  </button>
                  <button
                    onClick={handleUseAsImg2Img}
                    className="flex-shrink-0 px-3 py-1 bg-black/50 border border-white/30 rounded-[2px] text-white text-xs"
                  >
                    {translate('cn_face_img2_send')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-1 flex-col w-full items-center px-4">
                <div className="flex-1 flex flex-col items-center justify-center">
                  <span className="text-xs text-[#7f7f7f] text-center">
                    {translate('Upload an image with a face to reference its pose, position, and shots')}
                  </span>
                </div>
                <button
                  onClick={handlePickReferenceImage}
                  className="w-32 h-[34px] mb-[46px] bg-white border border-[#ededed] hover:border-[#cdcdcd] rounded text-[#2d2d2d] text-xs font-grotesk"
                >
                  {translate('imgref_upload')}
                </button>
              </div>
            ))}
        </div>
      </div>
      <div className="w-10">
        <GuidePopover
          visible={guideVisible && isSimple}
          title={translate('imgref_advancetips_title')}
          position="top"
          width={260}
          showFooter={false}
          offset={{ mainAxis: 0, crossAxis: 100 }}
          content={translate('imgref_advancetips_content')}
        />
      </div>
      <button
        disabled={isSimple}
        onMouseOver={() => setGuideVisible(true)}
        onMouseLeave={() => setGuideVisible(false)}
        onClick={() => setAdvancedParamsVisible(!advancedParamsVisible)}
        className="flex w-full items-center justify-between h-[50px] px-4 disabled:opacity-40"
      >
        <span className="text-xs text-[#2d2d2d] font-medium group-disabled:opacity-40">
          {translate('cn_all_params')}
        </span>
        <Icon
          className={`icon-comfy_xiala ${advancedParamsVisible ? 'rotate-180' : ''}`}
          fontSize={14}
          color="#2d2d2d"
        />
      </button>
      {showAdvancedParams && (
        <>
          <div className="mx-4 mb-3">
            <span className="text-xs text-[#2d2d2d] font-medium">{translate('cn_face_img1')}</span>
          </div>
          <Options options={faceEmbedingOptions} controlNet={controlNet} onChange={handleChangeOption} />
          <div className="mx-4 mb-3 pt-3 border-t border-[#efefef]">
            <span className="text-xs text-[#2d2d2d] font-medium">{translate('cn_face_img2')}</span>
          </div>
          <Options options={faceKeypointsOptions} controlNet={controlNet} onChange={handleChangeOption} />
        </>
      )}
    </div>
  );
});
